import React from 'react'
// libs
import { change } from 'redux-form'
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
import { useMutation, useSubscription } from '@apollo/client/react'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// graphql
import { APP_NOTIFICATION } from 'graphql/notifications/subscriptions'
import { CHECK_INLINE_FUNCTIONS, CHECK_INLINE_FUNCTIONS_PUBLIC } from 'graphql/systemProcesses/mutations'
// constants
import { Forms } from 'constants/index'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
// types
import { RiskNotificationType } from 'types/risk'
import { IField, INotification } from 'types/graphql'

type SubscriptionResponse = {
  notification: INotification
}

export type FieldValueToUpdate = {
  fieldId: string
  fieldValue: string
  fieldLinkId: string | null
  fieldValueId: string
  parentFieldValueId: string | null
}

export type InlineFunction = {
  id: string
  name: string
  field: IField
  fieldLinkId: string
  fieldValueId: string
}

export const useRiskNotifications = (riskId?: string, riskTemplateId?: string, skipValueState = false) => {
  const user = useCurrentUser()
  const router = useRouter()
  const dispatch = useDispatch()

  const isPublic = React.useContext(PublicContext)

  const { data: subscriptionData } = useSubscription<SubscriptionResponse>(APP_NOTIFICATION, {
    shouldResubscribe: true,
    skip: !riskId,
    variables: { eventType: `${riskId}:${isPublic ? router.query.domain : user.company.domainName}` }
  })

  const [checkInlineFunctions] = useMutation(isPublic ? CHECK_INLINE_FUNCTIONS_PUBLIC : CHECK_INLINE_FUNCTIONS)

  const [inlineFunctions, setInlineFunctions] = React.useState<InlineFunction[]>([])
  const [fieldValueToUpdate, setFieldValueToUpdate] = React.useState<FieldValueToUpdate | null>(null)

  const clearFieldValueToUpdate = () => setFieldValueToUpdate(null)

  React.useEffect(() => {
    if (riskId && riskTemplateId) {
      setTimeout(
        () =>
          checkInlineFunctions({
            variables: {
              ...(isPublic && { domain: router.query.domain }),
              input: {
                riskId,
                riskTemplateId
              }
            }
          }),
        1000
      )
    }
  }, [riskId, riskTemplateId])

  React.useEffect(() => {
    if (subscriptionData) {
      const notification = subscriptionData?.notification?.payload

      if (notification?.notificationType === RiskNotificationType.ShowFunction) {
        setInlineFunctions((prev) => {
          if (notification.shouldShow) {
            return [
              ...prev.filter(
                (inlineFn) =>
                  inlineFn.id !== notification.transaction.inlineFunctionId ||
                  notification.fieldValues.some((item) => item.fieldValueId === inlineFn.fieldValueId)
              ),
              ...notification.fieldValues
                .filter(
                  (item) =>
                    !prev.some(
                      (inlineFn) =>
                        inlineFn.id === notification.transaction.inlineFunctionId &&
                        inlineFn.fieldValueId === item.fieldValueId
                    )
                )
                .map((item) => ({
                  ...item,
                  id: notification.transaction.inlineFunctionId,
                  name: notification.transaction.functionName,
                  position: notification.transaction.position
                }))
            ]
          }

          return prev.filter(
            (inlineFn) =>
              inlineFn.id !== notification.transaction.inlineFunctionId ||
              notification.fieldValues.every((item) => item.fieldValueId !== inlineFn.fieldValueId)
          )
        })
      }

      if (notification?.notificationType === RiskNotificationType.SetField) {
        if (notification.fieldLinkId && !skipValueState) {
          setFieldValueToUpdate(notification)
        } else {
          dispatch(
            change(Forms.RISK_TEMPLATE_FORM, `${skipValueState ? notification.field.id : notification.fieldName}`, {
              value: notification.fieldValue,
              fieldValueId: notification.fieldValueId,
              ...(skipValueState && {
                fieldLinkId: notification.fieldLinkId,
                parentFieldValueId: notification.parentFieldValueId
              })
            })
          )
        }
      }
    }
  }, [subscriptionData])

  return { inlineFunctions, fieldValueToUpdate, clearFieldValueToUpdate }
}
